import { CheckCircle2, Copy, Download, Loader2 } from 'lucide-react';
import {
  Children,
  isValidElement,
  memo,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode
} from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ChatMarkdownProps {
  text: string;
  streaming?: boolean;
}

interface StreamingMarkdownView {
  markdown: string;
  pendingTable: boolean;
}

const remarkPlugins = [remarkGfm];

const components: Components = {
  a({ node: _node, href, children, ...props }) {
    if (!href) {
      return <span>{children}</span>;
    }

    const external = /^https?:\/\//i.test(href ?? '');
    return (
      <a
        {...props}
        href={href}
        rel={external ? 'noreferrer noopener' : undefined}
        target={external ? '_blank' : undefined}
      >
        {children}
      </a>
    );
  },
  h1({ node: _node, children, ...props }) {
    return <h3 {...props}>{children}</h3>;
  },
  h2({ node: _node, children, ...props }) {
    return <h3 {...props}>{children}</h3>;
  },
  h3({ node: _node, children, ...props }) {
    return <h4 {...props}>{children}</h4>;
  },
  h4({ node: _node, children, ...props }) {
    return <h5 {...props}>{children}</h5>;
  },
  img({ node: _node, alt }) {
    return <span className="chat-image-placeholder">Image omitted{alt ? `: ${alt}` : ''}</span>;
  },
  pre({ node: _node, children, ...props }) {
    return <ChatCodeBlock {...props}>{children}</ChatCodeBlock>;
  },
  table({ node: _node, children, ...props }) {
    return <ChatDataTable {...props}>{children}</ChatDataTable>;
  },
  th({ node: _node, children, ...props }) {
    return <th {...props} scope="col">{children}</th>;
  }
};

export const ChatMarkdown = memo(function ChatMarkdown({ text, streaming = false }: ChatMarkdownProps): ReactNode {
  const view = useMemo(() => prepareStreamingMarkdown(text, streaming), [streaming, text]);

  return (
    <div className={`chat-message-markdown${streaming ? ' streaming' : ''}`}>
      {view.markdown && (
        <ReactMarkdown
          components={components}
          remarkPlugins={remarkPlugins}
          skipHtml
          urlTransform={safeChatUrl}
        >
          {view.markdown}
        </ReactMarkdown>
      )}
      {view.pendingTable && <PendingTable />}
      {streaming && (
        <span className="chat-stream-indicator" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      )}
    </div>
  );
});

function ChatDataTable({ children, ...props }: ComponentPropsWithoutRef<'table'>): ReactNode {
  const tableRef = useRef<HTMLTableElement>(null);
  const [copied, setCopied] = useState(false);

  const copyTable = async () => {
    const rows = readTableRows(tableRef.current);
    if (rows.length === 0) {
      return;
    }

    try {
      await navigator.clipboard.writeText(rows.map((row) => row.join('\t')).join('\n'));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard access can be unavailable in locked-down browser contexts.
    }
  };

  const downloadTable = () => {
    const rows = readTableRows(tableRef.current);
    if (rows.length === 0) {
      return;
    }

    const csv = rows
      .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    downloadTextFile('iasset-response-table.csv', `\uFEFF${csv}`, 'text/csv;charset=utf-8');
  };

  return (
    <section className="chat-data-table" aria-label="Response data table">
      <header className="chat-data-table-toolbar">
        <span>Data table</span>
        <div>
          <button type="button" onClick={() => void copyTable()}>
            {copied ? <CheckCircle2 /> : <Copy />}
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button type="button" onClick={downloadTable}>
            <Download />
            CSV
          </button>
        </div>
      </header>
      <div className="chat-data-table-scroll" role="region" aria-label="Scrollable response data" tabIndex={0}>
        <table {...props} ref={tableRef}>
          <caption className="sr-only">Data returned in the AI response</caption>
          {children}
        </table>
      </div>
    </section>
  );
}

function ChatCodeBlock({ children, ...props }: ComponentPropsWithoutRef<'pre'>): ReactNode {
  const [copied, setCopied] = useState(false);
  const language = getCodeLanguage(children);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(getReactNodeText(children).replace(/\n$/, ''));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard access can be unavailable in locked-down browser contexts.
    }
  };

  return (
    <section className="chat-code-block" aria-label={language ? `${language} code` : 'Code block'}>
      <header>
        <span>{language || 'Code'}</span>
        <button type="button" onClick={() => void copyCode()}>
          {copied ? <CheckCircle2 /> : <Copy />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </header>
      <pre {...props}>{children}</pre>
    </section>
  );
}

function PendingTable(): ReactNode {
  return (
    <div className="chat-table-building" role="status" aria-label="Formatting data table">
      <div>
        <Loader2 />
        <span>Formatting data table</span>
      </div>
      <span />
      <span />
      <span />
    </div>
  );
}

function prepareStreamingMarkdown(markdown: string, streaming: boolean): StreamingMarkdownView {
  const normalized = markdown.replace(/\r\n/g, '\n');
  if (!streaming || !normalized) {
    return { markdown: normalized, pendingTable: false };
  }

  const lines = normalized.split('\n');
  let candidateStart = lines.length;
  while (candidateStart > 0 && isPotentialTableRow(lines[candidateStart - 1])) {
    candidateStart -= 1;
  }

  if (candidateStart === lines.length) {
    return { markdown: normalized, pendingTable: false };
  }

  const candidateLines = lines.slice(candidateStart);
  const hasCompleteDelimiter = candidateLines.length > 1 && isTableDelimiter(candidateLines[1]);
  if (hasCompleteDelimiter) {
    return { markdown: normalized, pendingTable: false };
  }

  return {
    markdown: lines.slice(0, candidateStart).join('\n').trimEnd(),
    pendingTable: true
  };
}

function isTableDelimiter(line: string): boolean {
  const cells = line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());

  return cells.length > 1 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function isPotentialTableRow(line: string): boolean {
  const trimmed = line.trim();
  return Boolean(trimmed && !trimmed.startsWith('```') && trimmed.includes('|'));
}

function safeChatUrl(url: string): string {
  const value = url.trim();
  if (
    /^(?:https?:|mailto:)/i.test(value)
    || /^(?:#|\/(?!\/)|\.{1,2}\/)/.test(value)
  ) {
    return value;
  }

  return '';
}

function readTableRows(table: HTMLTableElement | null): string[][] {
  if (!table) {
    return [];
  }

  return Array.from(table.rows).map((row) => (
    Array.from(row.cells).map((cell) => cell.textContent?.replace(/\s+/g, ' ').trim() ?? '')
  ));
}

function getCodeLanguage(children: ReactNode): string {
  const child = Children.toArray(children)[0];
  if (!isValidElement<{ className?: string }>(child)) {
    return '';
  }

  return /^language-(.+)$/.exec(child.props.className ?? '')?.[1] ?? '';
}

function getReactNodeText(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(getReactNodeText).join('');
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    return getReactNodeText(node.props.children);
  }

  return '';
}

function downloadTextFile(fileName: string, content: string, contentType: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: contentType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
